import { ChangeEvent, FC, useEffect, useState } from "react";
import { Box, Checkbox, Collapse, FormControlLabel, Grid, Stack, TextField, Typography } from "@mui/material";

import { useAppDispatch, useAppSelector } from "../../state/store";
import "../../index.css";
import { useEstimatorFormStyles } from "./EstimatorForm.styles";
import { inksState, machineConfigState, sernumsState, setInkFields, setInkTank } from "../../state/sernums/sernums.slice";

export interface EstimatiorFormValues {
  numberOfCopies: string;
  avgCoverage: string;
  mediaSize: string;
  mediaSizeError: string;
  file: File | null;
  polymer: string[];
  clean: boolean;
  fileName: string;
  layers: string;
  markAllImage: boolean;
}

const EstimatorForm: FC<{ showAdvance: boolean }> = ({ showAdvance }) => {
  const sx = useEstimatorFormStyles();
  const dispatch = useAppDispatch();

  const sernums = useAppSelector(sernumsState);
  const inkFields = useAppSelector(inksState);
  const machineConfig = useAppSelector(machineConfigState);

  const [selectedCheckbox, setSelectedCheckbox] = useState<string | null>(null);
  const [_, setSelectionOrder] = useState<string[]>(["", "", "", "", ""]);

  const [selectedPolymer, setSelectedPolymer] = useState<Record<string, boolean>>({
    SPOT80: false,
    PS100: false,
    PS300: false,
    PS350: false,
    PS380: false,
    PS500: false,
    PS510: false,
    PS520: false,
    PS550: false,
    PS800: false,
    PS850: false,
    PS900: false,
    PS2000: false
  });

  const handleCheckboxChange = (id: string) => {
    const findInkTank = inkFields.find((ink) => ink.id === id);
    if (!findInkTank) return;

    const isFirstTank = findInkTank.id === "cleaningFluid" || findInkTank.label === "Tank1";
    const hasPolymerValue = !!findInkTank.value;

    // If a non-cleaning tank is selected without a polymer value, show an error and do not select it
    if (!isFirstTank && !hasPolymerValue) {
      window.alert("Please assign a polymer type to this tank before selecting it.");
      return;
    }

    dispatch(
      setInkTank({
        label: findInkTank.label,
        value: isFirstTank ? "Cleaning Fluid" : findInkTank.value
      })
    );

    setSelectedCheckbox((prev) => (prev === id ? null : id));
  };

  const handlePolymerCheckboxChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target;

    setSelectedPolymer((prevState) => {
      const selectedCount = Object.values(prevState).filter(Boolean).length;
      if (checked && selectedCount >= 4) {
        return prevState;
      }

      const updatedState = { ...prevState, [name]: checked };

      setSelectionOrder((prevOrder) => {
        const newOrder = [...prevOrder];

        if (checked) {
          const firstEmptyIndex = newOrder.findIndex((key) => key === "");
          if (firstEmptyIndex !== -1) {
            newOrder[firstEmptyIndex] = name;
          }
        } else {
          const polymerIndex = newOrder.indexOf(name);
          if (polymerIndex !== -1) {
            newOrder[polymerIndex] = "";
          }
        }

        dispatch(setInkFields(newOrder));

        return newOrder;
      });

      return updatedState;
    });
  };

  const hasYValue = Object.values(sernums).some((value) => value === "Y");

  return (
    <Stack component="form" gap={2}>
      <Stack sx={sx.inputBoxes}>
        <Stack direction="row" gap={6}>
          <Typography variant="subtitle2">Machine Configuration</Typography>
          <Stack direction="row" gap={4}>
              {machineConfig.machineType && (
                <Typography variant="subtitle2">
                  Machine Type: {machineConfig.machineType}
                </Typography>
              )}
              {machineConfig.cameraType && (
                <Typography variant="subtitle2">
                  Camera Type: {machineConfig.cameraType}
                </Typography>
              )}
              {machineConfig.w2pType && (
                <Typography variant="subtitle2">
                  Studio Type: {machineConfig.w2pType}
                </Typography>
              )}
              {machineConfig.foilerType && (
                <Typography variant="subtitle2">
                  Foiler Type: {machineConfig.foilerType}
                </Typography>
              )}
          </Stack>
        </Stack>
        <Grid container>
          {Object.entries(sernums)
          .filter(([key]) => !["MAESTROEMAIL", "VMCOUNT","STORAGEWINID","MAESTROPASS","SHIPDATE", "W2PTYPE","CDES","WINID","INHOUSEPRESS","UIPRESSTYPEDES","FOILERTYPE","CAMERATYPE","PRESSTYPEDES","STUDIOID","W2PWINID","Foiler"].includes(key))
          .map(([key, value]) => (
            <Grid item xs={6} sm={4} md={2} key={`sernum_${key}`}>
              <FormControlLabel label={key} control={<Checkbox checked={hasYValue && (value === "Y" || value !== null)} />} />
            </Grid>
          ))}
        </Grid>
      </Stack>

      <Stack sx={sx.inputBoxes}>
        <Collapse in={showAdvance}>
          <Stack
            direction="row"
            gap={1}
            flexWrap="wrap"
            sx={{
              "@media (max-width:600px)": {
                flexDirection: "column"
              }
            }}>
            <Stack sx={sx.inputBoxes} flex={2}>
              <Typography variant="subtitle2">Polymer Type</Typography>
              <Grid container>
                {Object.keys(selectedPolymer).map((type) => (
                  <Grid key={`polymer${type}`} item xs={6} sm={6} md={2}>
                    <FormControlLabel label={type} control={<Checkbox name={type} checked={selectedPolymer[type]} onChange={handlePolymerCheckboxChange} />} />
                  </Grid>
                ))}
              </Grid>
            </Stack>

            <Stack sx={sx.inputBoxes} flex={2}>
              <Typography variant="subtitle2">Ink Tanks</Typography>
              <Grid container gap={1}>
                {inkFields.map((item) => (
                  <Grid key={item.id} item sm={6} md={3}>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Checkbox
                      checked={selectedCheckbox === item.id}
                      onChange={() => handleCheckboxChange(item.id)}
                      sx={{ padding: 0 }} // Removes extra padding around the checkbox
                    />
                    <Typography variant="body2" sx={{ marginLeft: "-4px" }}>
                      {item.label}
                      </Typography>
                      <TextField placeholder={item.placeholder} value={item.value} disabled />
                    </Stack>
                  </Grid>
                ))}
              </Grid>
            </Stack>
          </Stack>
        </Collapse>
      </Stack>
    </Stack>
  );
};

export default EstimatorForm;
